import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ColorPicker from '../components/ColorPicker';
import Slider from '../components/Slider';
import Toggle from '../components/Toggle';
import TextInput from '../components/TextInput';
import { mockupService } from '../services/mockupService';
import StepProgress from '../components/StepProgress';
import { ArrowLeft } from 'lucide-react';
import PromptHelper from '../components/PromptHelper';

const Customize: React.FC = () => {
  const navigate = useNavigate();
  
  // Customization states
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [productColor, setProductColor] = useState('#3b82f6');
  const [showShadow, setShowShadow] = useState(true);
  const [showReflection, setShowReflection] = useState(false);
  const [glossyFinish, setGlossyFinish] = useState(false);
  const [embossEffect, setEmbossEffect] = useState(false);
  const [gradientOverlay, setGradientOverlay] = useState(false);
  const [productTitle, setProductTitle] = useState('');
  const [productSubtitle, setProductSubtitle] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [scale, setScale] = useState(100);
  const [aspectRatio, setAspectRatio] = useState('square');
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [primaryImageId, setPrimaryImageId] = useState('');
  const [secondaryImageId, setSecondaryImageId] = useState<string | null>(null);

  // Load template and file info from session storage
  useEffect(() => {
    const template = sessionStorage.getItem('selectedTemplate');
    const pId = sessionStorage.getItem('primaryImageId');
    const sId = sessionStorage.getItem('secondaryImageId');
    const brandColorsStr = sessionStorage.getItem('brandColors');
    
    if (!template || !pId) {
      navigate('/dashboard/create');
      return;
    }
    
    setSelectedTemplate(template);
    setPrimaryImageId(pId);
    // Only set secondary image ID if it exists and is not empty
    setSecondaryImageId(sId && sId.trim() !== '' ? sId : null);
    
    // Load brand colors if available
    if (brandColorsStr) {
      try {
        const brandColors = JSON.parse(brandColorsStr);
        if (brandColors.primary) {
          setBackgroundColor(brandColors.primary);
        }
        if (brandColors.secondary) {
          setProductColor(brandColors.secondary);
        }
        console.log('Loaded brand colors:', brandColors);
      } catch (err) {
        console.error('Failed to parse brand colors:', err);
      }
    }
  }, [navigate]);

  const handleGenerate = async () => {
    if (!selectedTemplate || !primaryImageId) {
      alert('Missing template or primary image');
      return;
    }

    setIsGenerating(true);
    
    try {
      // Handle brand logo and asset library cases
      const useBrandLogo = sessionStorage.getItem('useBrandLogo') === 'true';
      const isAssetLibrary = sessionStorage.getItem('isAssetLibrary') === 'true';
      const primaryImageUrl = sessionStorage.getItem('primaryImageUrl');
      const secondaryImageUrl = sessionStorage.getItem('secondaryImageUrl');
      
      const generateOptions: any = {
        primaryImageId: primaryImageId,
        template: selectedTemplate,
        backgroundColor,
        productColor,
        productTitle,
        productSubtitle,
        customPrompt,
        showShadow,
        showReflection,
        glossyFinish,
        embossEffect,
        gradientOverlay,
        scale,
        aspectRatio
      };
      
      // Only add secondaryImageId if it exists
      if (secondaryImageId && secondaryImageId.trim() !== '') {
        generateOptions.secondaryImageId = secondaryImageId;
      }
      
      // Add primaryImageUrl if using brand logo or asset library
      if ((useBrandLogo || isAssetLibrary) && primaryImageUrl) {
        generateOptions.primaryImageUrl = primaryImageUrl;
      }
      
      // Add secondaryImageUrl if from asset library
      if (secondaryImageUrl) {
        generateOptions.secondaryImageUrl = secondaryImageUrl;
      }
      
      const response = await mockupService.generateMockup(generateOptions);

      if (response.success && response.mockups && response.mockups.length > 0) {
        sessionStorage.setItem('generatedMockupId', response.mockups[0].id);
        sessionStorage.setItem('creationStep', '3');
        navigate('/dashboard/download');
      } else {
        throw new Error(response.error || 'Failed to generate mockup');
      }
    } catch (error) {
      console.error('Error generating mockup:', error);
      alert('Failed to generate mockup. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const steps = [
    { number: 1, title: 'Choose Template', description: 'Select mockup type' },
    { number: 2, title: 'Upload Assets', description: 'Add your images' },
    { number: 3, title: 'Customize', description: 'Adjust settings' }
  ];

  return (
    <div>
      <StepProgress currentStep={3} steps={steps} />
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/dashboard/create')}
            className="flex items-center text-body-grey hover:text-text-dark transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Upload
          </button>
          {selectedTemplate && (
            <span className="ml-auto text-sm text-body-grey">
              Template: <span className="font-medium text-text-dark capitalize">{selectedTemplate.replace(/-/g, ' ')}</span>
            </span>
          )}
        </div>
        
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Customize Your Mockup
          </h1>
          <p className="text-lg text-gray-600">
            Fine-tune your mockup to perfection
          </p>
        </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Preview Section */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div 
              className="aspect-square rounded-lg flex items-center justify-center relative overflow-hidden"
              style={{ backgroundColor }}
            >
              {/* Mockup preview */}
              <div className="text-center">
                <div className="text-6xl mb-4">
                  {selectedTemplate.includes('book') && '📚'}
                  {selectedTemplate.includes('software-box') && '📦'}
                  {selectedTemplate.includes('digital-box') && '🗃️'}
                  {selectedTemplate.includes('cd') && '💿'}
                  {selectedTemplate.includes('tshirt') && '👕'}
                  {selectedTemplate.includes('hoodie') && '🧥'}
                  {selectedTemplate.includes('cap') && '🧢'}
                  {selectedTemplate.includes('banner') && '🏳️'}
                  {selectedTemplate.includes('booth') && '🏢'}
                  {selectedTemplate.includes('product-stand') && '🗂️'}
                  {selectedTemplate.includes('mug') && '☕'}
                  {selectedTemplate.includes('business-card') && '🪪'}
                  {selectedTemplate.includes('phone-case') && '📱'}
                  {selectedTemplate.includes('laptop') && '💻'}
                  {selectedTemplate.includes('billboard') && '🏗️'}
                  {selectedTemplate.includes('shopping-bag') && '🛍️'}
                  {selectedTemplate.includes('poster') && '📄'}
                  {selectedTemplate.includes('bottle') && '🍶'}
                  {selectedTemplate.includes('wall-art') && '🖼️'}
                  {selectedTemplate.includes('stationery') && '📋'}
                  {!selectedTemplate && '📦'}
                </div>
                <p className="text-gray-600 font-medium mb-2">
                  {selectedTemplate.split('-').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ')} Mockup
                </p>
                {productTitle && (
                  <p className="mt-4 text-xl font-bold" style={{ color: productColor }}>
                    {productTitle}
                  </p>
                )}
                {productSubtitle && (
                  <p className="mt-1 text-sm" style={{ color: productColor, opacity: 0.8 }}>
                    {productSubtitle}
                  </p>
                )}
              </div>
              
              {showShadow && (
                <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/10 to-transparent" />
              )}
              
              {isGenerating && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="bg-white rounded-lg p-6 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                    <p className="text-gray-900 font-semibold">Generating your mockup...</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Customization Controls */}
        <div className="space-y-6">
          {/* Colors Section */}
          <div className="bg-white rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Colors</h3>
            <ColorPicker
              label="Background Color"
              color={backgroundColor}
              onChange={setBackgroundColor}
            />
            <ColorPicker
              label="Product Color"
              color={productColor}
              onChange={setProductColor}
            />
          </div>

          {/* Text Overlay Section */}
          <div className="bg-white rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Text</h3>
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> Text will be professionally integrated into the mockup design, not overlaid afterwards.
              </p>
            </div>
            <TextInput
              label="Product Title"
              value={productTitle}
              onChange={setProductTitle}
              placeholder="Enter product name"
              maxLength={50}
              description="Main title that will be incorporated into the mockup design"
            />
            <TextInput
              label="Product Tagline"
              value={productSubtitle}
              onChange={setProductSubtitle}
              placeholder="Enter tagline or subtitle"
              maxLength={100}
              description="Secondary text that complements your product title"
            />
          </div>

          {/* Adjustments Section */}
          <div className="bg-white rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Adjustments</h3>
            <Slider
              label="Logo Scale"
              value={scale}
              onChange={setScale}
              min={50}
              max={150}
              step={5}
              unit="%"
            />
            <div className="pt-2">
              <label className="text-sm font-medium text-gray-700 block mb-2">Aspect Ratio</label>
              <div className="flex space-x-2">
                {['square', 'landscape', 'portrait'].map((ratio) => (
                  <button
                    key={ratio}
                    onClick={() => setAspectRatio(ratio)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      aspectRatio === ratio
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {ratio.charAt(0).toUpperCase() + ratio.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Effects Section */}
          <div className="bg-white rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Effects</h3>
            <Toggle
              label="Shadow Effect"
              description="Add realistic shadows for depth"
              checked={showShadow}
              onChange={setShowShadow}
            />
            <Toggle
              label="Reflection"
              description="Add a subtle reflection beneath the product"
              checked={showReflection}
              onChange={setShowReflection}
            />
            <Toggle
              label="Glossy Finish"
              description="Apply a glossy, high-shine finish to the product"
              checked={glossyFinish}
              onChange={setGlossyFinish}
            />
            <Toggle
              label="Emboss/Deboss Effect"
              description="Add raised or recessed texture to the design"
              checked={embossEffect}
              onChange={setEmbossEffect}
            />
            <Toggle
              label="Gradient Overlay"
              description="Apply a subtle gradient effect to the product"
              checked={gradientOverlay}
              onChange={setGradientOverlay}
            />
          </div>

          {/* Advanced Options */}
          <div className="bg-white rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Advanced</h3>
            
            {/* Prompt Helper */}
            <PromptHelper 
              template={selectedTemplate || ''}
              onSuggestionClick={(suggestion) => {
                // Add suggestion to existing prompt with proper formatting
                setCustomPrompt(prev => {
                  if (prev) {
                    return `${prev}, ${suggestion}`;
                  }
                  return suggestion;
                });
              }}
            />
            
            <TextInput
              label="Custom Instructions"
              value={customPrompt}
              onChange={setCustomPrompt}
              placeholder="e.g., 'Professional fashion photography, soft studio lighting, minimalist backdrop'"
              description="Use specific descriptions for best results. Click suggestions above for ideas."
            />
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="w-full px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400"
            >
              {isGenerating ? 'Generating...' : 'Generate Mockup'}
            </button>
            <button
              onClick={() => navigate('/templates')}
              className="w-full px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Back to Templates
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Customize;
